"use client"

import { type ReactNode, useEffect, useState } from "react"
import { usePathname, useRouter } from "next/navigation"
import { signOut, useSession } from "next-auth/react"
import { toast } from "sonner"

import { LoadingScreen } from "@/components/loading-screen"
import { isSessionExpired } from "@/lib/sessionUtils"

interface AuthGuardProviderProps {
  children: ReactNode
}

export function AuthGuardProvider({ children }: AuthGuardProviderProps) {
  const { data: session, status } = useSession()
  const pathname = usePathname()
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(true)
  const [isRedirectingToLogin, setIsRedirectingToLogin] = useState(false)
  const isAuthPage = pathname.startsWith("/auth")

  const handleSessionExpiration = () => {
    if (isRedirectingToLogin || isAuthPage) return

    setIsRedirectingToLogin(true)
    toast.error("Your session has expired. Please sign in again.")

    signOut({
      callbackUrl: "/auth/signin",
      redirect: false,
    }).then(() => {
      router.replace("/auth/signin")
    })
  }

  const isInvalidSession =
    status === "authenticated" &&
    !!session &&
    (isSessionExpired(session) || !session.user?.id || session.error === "UserNotFound")

  useEffect(() => {
    if (isInvalidSession && !isAuthPage && !isRedirectingToLogin) {
      handleSessionExpiration()
      setIsLoading(false)
      return
    }

    if (status !== "loading") {
      setIsLoading(false)
    }
  }, [session, status, isAuthPage, isRedirectingToLogin, isInvalidSession])

  useEffect(() => {
    if (!session || status !== "authenticated" || isRedirectingToLogin) return

    const checkSessionExpiry = () => {
      if (isSessionExpired(session) && !isAuthPage && !isRedirectingToLogin) {
        handleSessionExpiration()
      }
    }

    const interval = setInterval(checkSessionExpiry, 5000)

    return () => clearInterval(interval)
  }, [session, status, isRedirectingToLogin, isAuthPage])

  useEffect(() => {
    if (isAuthPage && isRedirectingToLogin) {
      setIsRedirectingToLogin(false)
    }
  }, [isAuthPage, isRedirectingToLogin])

  const stillLoading = isLoading || isRedirectingToLogin || status === "loading"

  if (stillLoading) {
    return <LoadingScreen />
  }

  if (status === "unauthenticated" && !isAuthPage && !isRedirectingToLogin) {
    router.replace("/auth/signin")
    return null
  }

  return <>{children}</>
}
